import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Users, Plus, Mail, Search, Trash2, MapPin, Coins, Pencil, Network, Table2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmployeeStatistics } from "./employee-management/EmployeeStatistics";
import { CSVImport } from "./employee-management/CSVImport";
import { OrgChart } from "./employee-management/OrgChart";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
  maximum_points_per_employee?: number;
}

interface Employee {
  id: string;
  invitation_status: string;
  invited_at: string;
  accepted_at: string | null;
  first_name?: string;
  last_name?: string;
  email?: string;
  position?: string;
  department?: string;
  residence_country?: string;
  current_country?: string;
  manager_id?: string | null;
  seniority_level_id?: string | null;
  profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    position: string | null;
  } | null;
  remote_work_requests?: Array<{
    id: string;
    start_date: string;
    end_date: string;
    status: string;
    destination_country: string;
  }>;
  employee_points?: Array<{
    total_points: number;
    used_points: number;
    month: number;
    year: number;
  }>;
}

const DEPARTMENTS = [
  { value: 'HR', label: 'Ressources Humaines' },
  { value: 'Engineering', label: 'Engineering' },
  { value: 'Product', label: 'Produit' },
  { value: 'Design', label: 'Design' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Legal', label: 'Juridique' },
  { value: 'Operations', label: 'Opérations' },
  { value: 'Support', label: 'Support Client' },
];

interface EditEmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  department: string;
  residenceCountry: string;
  seniorityLevelId: string;
  managerId: string;
}

interface ManagerOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  position?: string;
}

interface EmployeesTabProps {
  company: Company | null;
}

const EmployeesTab = ({ company }: EmployeesTabProps) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [managerOptions, setManagerOptions] = useState<ManagerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [editEmployeeData, setEditEmployeeData] = useState<EditEmployeeData>({
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    department: "",
    residenceCountry: "",
    seniorityLevelId: "",
    managerId: ""
  });
  const [newEmployee, setNewEmployee] = useState({
    firstName: "",
    lastName: "",
    email: "",
    position: "",
    department: "",
    residenceCountry: "",
    nestPoints: "10",
    seniorityLevelId: "",
    managerId: ""
  });
  const [seniorityLevels, setSeniorityLevels] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchEmployees();
      fetchSeniorityLevels();
    }
  }, [company]);

  // Update manager options when employees list changes
  useEffect(() => {
    // All employees can potentially be managers - filter out just the display data
    const options: ManagerOption[] = employees.map(emp => ({
      id: emp.id,
      first_name: emp.first_name || '',
      last_name: emp.last_name || '',
      email: emp.email || '',
      position: emp.position || ''
    }));
    setManagerOptions(options);
  }, [employees]);

  const fetchSeniorityLevels = async () => {
    if (!company) return;
    try {
      const { data, error } = await supabase
        .from('company_seniority_levels')
        .select('*')
        .eq('company_id', company.id)
        .order('sort_order', { ascending: true });
      
      if (error) throw error;
      setSeniorityLevels(data || []);
    } catch (error: any) {
      console.error('Error fetching seniority levels:', error);
    }
  };

  useEffect(() => {
    const filtered = employees.filter(employee => {
      const displayName = employee.first_name && employee.last_name 
        ? `${employee.first_name} ${employee.last_name}`
        : employee.profiles 
        ? `${employee.profiles.first_name} ${employee.profiles.last_name}`
        : '';
      
      const displayEmail = employee.email || 
        (employee.profiles ? employee.profiles.email : '');
      
      const displayPosition = employee.position || 
        (employee.profiles ? employee.profiles.position : '') || '';

      return (
        displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        displayEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        displayPosition.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setFilteredEmployees(filtered);
  }, [employees, searchTerm]);

  const fetchEmployees = async () => {
    if (!company) return;
    
    try {
      // First, fetch employees
      const { data, error } = await supabase
        .from('employees')
        .select(`
          id,
          invitation_status,
          invited_at,
          accepted_at,
          first_name,
          last_name,
          email,
          position,
          department,
          residence_country,
          manager_id,
          seniority_level_id,
          profiles:profile_id (
            first_name,
            last_name,
            email,
            position
          ),
          employee_points (
            total_points,
            used_points,
            month,
            year
          )
        `)
        .eq('company_id', company.id)
        .order('invited_at', { ascending: false });

      if (error) throw error;
      
      const typedData = (data || []) as unknown as typeof data;

      // Then fetch remote work requests separately using secure function
      const { data: remoteWorkRequests } = await supabase
        .rpc('get_company_remote_work_requests');
      
      // Process employees to determine current country and latest points
      const processedEmployees = (data || []).map(employee => {
        // Filter requests for this employee
        const employeeRequests = (remoteWorkRequests || []).filter(
          req => req.employee_id === employee.id
        );

        const approvedRemoteWork = employeeRequests.filter(
          req => req.status === 'approved'
        );
        
        const currentRemoteWork = approvedRemoteWork.find(req => {
          const startDate = new Date(req.start_date);
          const endDate = new Date(req.end_date);
          const today = new Date();
          return startDate <= today && endDate >= today;
        });
        
        // Get the latest employee_points record
        const latestPoints = employee.employee_points?.reduce((latest, current) => {
          if (!latest) return current;
          
          const latestDate = new Date(latest.year, latest.month - 1);
          const currentDate = new Date(current.year, current.month - 1);
          
          return currentDate > latestDate ? current : latest;
        }, null);
        
        return {
          ...employee,
          remote_work_requests: employeeRequests,
          current_country: currentRemoteWork?.destination_country || employee.residence_country,
          employee_points: latestPoints ? [latestPoints] : []
        };
      }) as unknown as Employee[];
      
      setEmployees(processedEmployees);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les employés"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!newEmployee.firstName || !newEmployee.lastName || !newEmployee.email) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    const points = parseInt(newEmployee.nestPoints) || 10;
    if (points < 0) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Le nombre de points doit être positif"
      });
      return;
    }

    try {
      // Insert employee
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .insert({
          company_id: company?.id,
          first_name: newEmployee.firstName,
          last_name: newEmployee.lastName,
          email: newEmployee.email,
          position: newEmployee.position || null,
          department: newEmployee.department || null,
          residence_country: newEmployee.residenceCountry || null,
          invitation_status: 'invitation_non_envoyee',
          seniority_level_id: newEmployee.seniorityLevelId || null,
          manager_id: newEmployee.managerId || null
        })
        .select('id')
        .single();

      if (employeeError) throw employeeError;

      // Create employee points record for current month
      const now = new Date();
      const { error: pointsError } = await supabase
        .from('employee_points')
        .insert({
          employee_id: employeeData.id,
          month: now.getMonth() + 1,
          year: now.getFullYear(),
          total_points: points,
          used_points: 0
        });

      if (pointsError) throw pointsError;

      toast({
        title: "Employé ajouté",
        description: `${newEmployee.firstName} ${newEmployee.lastName} a été ajouté avec ${points} Nest Points`
      });

      setIsDialogOpen(false);
      setNewEmployee({ firstName: "", lastName: "", email: "", position: "", department: "", residenceCountry: "", nestPoints: "10", seniorityLevelId: "", managerId: "" });
      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'ajouter l'employé"
      });
    }
  };

  const handleSendInvitation = async (employeeId: string, email: string, firstName: string, lastName: string) => {
    try {
      // Call edge function to send invitation email
      const { data, error } = await supabase.functions.invoke('send-employee-invitation', {
        body: {
          employeeId,
          employeeEmail: email,
          employeeFirstName: firstName,
          employeeLastName: lastName,
          companyName: company?.name || 'Votre entreprise',
          siteUrl: window.location.origin
        }
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Invitation envoyée",
        description: `Un email d'invitation a été envoyé à ${email}`
      });

      fetchEmployees();
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible d'envoyer l'invitation"
      });
    }
  };

  const handleDeleteEmployee = async (employeeId: string, name: string) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${name} ?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('employees')
        .delete()
        .eq('id', employeeId);

      if (error) throw error;

      toast({
        title: "Employé supprimé",
        description: `${name} a été supprimé de l'entreprise`
      });

      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'employé"
      });
    }
  };

  const openEditDialog = (employee: Employee) => {
    setEditingEmployee(employee);
    setEditEmployeeData({
      firstName: employee.first_name || '',
      lastName: employee.last_name || '',
      email: employee.email || '',
      position: employee.position || '',
      department: employee.department || '',
      residenceCountry: employee.residence_country || '',
      seniorityLevelId: employee.seniority_level_id || '',
      managerId: employee.manager_id || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee) return;

    if (!editEmployeeData.firstName || !editEmployeeData.lastName || !editEmployeeData.email) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires"
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('employees')
        .update({
          first_name: editEmployeeData.firstName,
          last_name: editEmployeeData.lastName,
          email: editEmployeeData.email,
          position: editEmployeeData.position || null,
          department: editEmployeeData.department || null,
          residence_country: editEmployeeData.residenceCountry || null,
          seniority_level_id: editEmployeeData.seniorityLevelId || null,
          manager_id: editEmployeeData.managerId || null
        })
        .eq('id', editingEmployee.id);

      if (error) throw error;

      toast({
        title: "Employé modifié",
        description: `${editEmployeeData.firstName} ${editEmployeeData.lastName} a été mis à jour`
      });

      setIsEditDialogOpen(false);
      setEditingEmployee(null);
      fetchEmployees();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de modifier l'employé"
      });
    }
  };

  const getManagerName = (managerId: string | null | undefined) => {
    if (!managerId) return null;
    const manager = employees.find(e => e.id === managerId);
    return manager ? `${manager.first_name} ${manager.last_name}` : null;
  };

  // Get team members (employees managed by a given employee)
  const getTeamMembers = (employeeId: string) => {
    return employees.filter(e => e.manager_id === employeeId);
  };

  const getStatusBadge = (status: string, acceptedAt: string | null) => {
    if (status === 'actif' || acceptedAt) {
      return <Badge className="bg-success text-success-foreground">Actif</Badge>;
    }
    
    switch (status) {
      case 'invitation_non_envoyee':
        return <Badge variant="outline">Non invité</Badge>;
      case 'invitation_envoyee':
        return <Badge variant="secondary">Invitation envoyée</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">En attente confirmation</Badge>;
      case 'en_attente_confirmation':
        return <Badge className="bg-yellow-100 text-yellow-800">En attente confirmation</Badge>;
      case 'force_approuve':
        return <Badge className="bg-blue-100 text-blue-800">Approuvé par manager</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="h-64 bg-muted rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Section */}
      <EmployeeStatistics 
        employees={employees} 
        company={company}
      />

      {/* Org Chart Section */}
      <OrgChart employees={employees} />

      {/* Employee Management Card */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion des collaborateurs
              </CardTitle>
              <CardDescription>
                Gérez les invitations et l'accès de vos {employees.length} employés
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <CSVImport 
                company={company}
                onImportComplete={fetchEmployees}
              />
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter un salarié
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Ajouter un nouvel employé</DialogTitle>
                    <DialogDescription>
                      Ajoutez un collaborateur à votre entreprise
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          value={newEmployee.firstName}
                          onChange={(e) => setNewEmployee(prev => ({...prev, firstName: e.target.value}))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          value={newEmployee.lastName}
                          onChange={(e) => setNewEmployee(prev => ({...prev, lastName: e.target.value}))}
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) => setNewEmployee(prev => ({...prev, email: e.target.value}))}
                        required
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="position">Poste</Label>
                        <Input
                          id="position"
                          value={newEmployee.position}
                          onChange={(e) => setNewEmployee(prev => ({...prev, position: e.target.value}))}
                          placeholder="Développeur, Manager, etc."
                        />
                      </div>
                      <div>
                        <Label htmlFor="department">Département</Label>
                        <Select 
                          value={newEmployee.department || "none"} 
                          onValueChange={(value) => setNewEmployee(prev => ({...prev, department: value === "none" ? "" : value}))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un département" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun</SelectItem>
                            {DEPARTMENTS.map(dept => (
                              <SelectItem key={dept.value} value={dept.value}>
                                {dept.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="residenceCountry">Pays de résidence</Label>
                      <Input
                        id="residenceCountry"
                        value={newEmployee.residenceCountry}
                        onChange={(e) => setNewEmployee(prev => ({...prev, residenceCountry: e.target.value}))}
                        placeholder="France, Espagne, etc."
                      />
                    </div>
                    <div>
                      <Label htmlFor="manager">Manager</Label>
                      <Select 
                        value={newEmployee.managerId || "none"} 
                        onValueChange={(value) => setNewEmployee(prev => ({...prev, managerId: value === "none" ? "" : value}))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner un manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun manager</SelectItem>
                          {managerOptions.map(manager => (
                            <SelectItem key={manager.id} value={manager.id}>
                              {manager.first_name} {manager.last_name} {manager.position ? `(${manager.position})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Le manager pourra valider les demandes de télétravail
                      </p>
                    </div>
                    {seniorityLevels.length > 0 && (
                      <div>
                        <Label htmlFor="seniorityLevel">Niveau de séniorité</Label>
                        <select
                          id="seniorityLevel"
                          value={newEmployee.seniorityLevelId}
                          onChange={(e) => setNewEmployee(prev => ({...prev, seniorityLevelId: e.target.value}))}
                          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">Aucun niveau</option>
                          {seniorityLevels.map(level => (
                            <option key={level.id} value={level.id}>
                              {level.level_name} ({level.monthly_points_credit} pts/mois)
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Définit les Nest Points mensuels automatiques selon la politique
                        </p>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="nestPoints">Nest Points initiaux à attribuer</Label>
                      <Input
                        id="nestPoints"
                        type="number"
                        min="0"
                        value={newEmployee.nestPoints}
                        onChange={(e) => setNewEmployee(prev => ({...prev, nestPoints: e.target.value}))}
                        placeholder="10"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Points de départ (les futurs crédits mensuels dépendront de la politique)
                      </p>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button onClick={handleAddEmployee}>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 mt-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        
        <CardContent>
          {filteredEmployees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {employees.length === 0 ? "Aucun employé pour le moment" : "Aucun employé ne correspond à votre recherche"}
              </p>
              {employees.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Commencez par inviter vos premiers employés
                </p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Poste / Département</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Pays actuel</TableHead>
                  <TableHead>Nest Points</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => {
                  const displayName = employee.first_name && employee.last_name 
                    ? `${employee.first_name} ${employee.last_name}`
                    : employee.profiles 
                    ? `${employee.profiles.first_name} ${employee.profiles.last_name}`
                    : 'Nom non défini';
                  
                  const displayEmail = employee.email || 
                    (employee.profiles ? employee.profiles.email : 'Email non défini');
                  
                  const displayPosition = employee.position || 
                    (employee.profiles ? employee.profiles.position : null) || '-';
                    
                  const displayDepartment = employee.department 
                    ? DEPARTMENTS.find(d => d.value === employee.department)?.label || employee.department 
                    : null;
                  
                  // Get points info
                  const employeePoints = employee.employee_points?.[0];
                  const availablePoints = employeePoints 
                    ? employeePoints.total_points - employeePoints.used_points 
                    : 0;

                  const managerName = getManagerName(employee.manager_id);
                  const teamMembers = getTeamMembers(employee.id);
                  
                  return (
                    <TableRow key={employee.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {displayName}
                          {teamMembers.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {teamMembers.length} membre{teamMembers.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{displayEmail}</TableCell>
                      <TableCell>
                        <div>
                          <span>{displayPosition}</span>
                          {displayDepartment && (
                            <Badge variant="outline" className="ml-2 text-xs">
                              {displayDepartment}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={employee.manager_id || "none"} 
                          onValueChange={async (value) => {
                            const newManagerId = value === "none" ? null : value;
                            try {
                              const { error } = await supabase
                                .from('employees')
                                .update({ manager_id: newManagerId })
                                .eq('id', employee.id);
                              
                              if (error) throw error;
                              
                              toast({
                                title: newManagerId ? "Manager assigné" : "Manager retiré",
                                description: newManagerId 
                                  ? `Manager assigné pour ${employee.first_name || ''} ${employee.last_name || ''}`
                                  : `Manager retiré pour ${employee.first_name || ''} ${employee.last_name || ''}`
                              });
                              
                              fetchEmployees();
                            } catch (error: any) {
                              toast({
                                variant: "destructive",
                                title: "Erreur",
                                description: error.message || "Impossible d'assigner le manager"
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="w-[180px] h-8 text-xs">
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="text-muted-foreground">Aucun</span>
                            </SelectItem>
                            {managerOptions
                              .filter(m => m.id !== employee.id) // Can't be their own manager
                              .map(manager => (
                                <SelectItem key={manager.id} value={manager.id}>
                                  {manager.first_name} {manager.last_name}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-primary" />
                          <span className="text-sm font-medium">
                            {employee.current_country || employee.residence_country || 'Non renseigné'}
                          </span>
                          {employee.current_country && employee.current_country !== employee.residence_country && (
                            <Badge variant="outline" className="text-xs">
                              Télétravail
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                       <TableCell>
                        <div className="flex items-center gap-2">
                          <Coins className="h-3 w-3 text-warning" />
                          <span className="text-sm font-medium">
                            {availablePoints}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            /{company?.maximum_points_per_employee || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(employee.invitation_status, employee.accepted_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(employee)}
                            title="Modifier"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {employee.invitation_status === 'invitation_non_envoyee' && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleSendInvitation(employee.id, employee.email || '', employee.first_name || '', employee.last_name || '')}
                              title="Inviter"
                            >
                              <Mail className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteEmployee(employee.id, displayName)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Employee Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier l'employé</DialogTitle>
            <DialogDescription>
              {editingEmployee && `Modifier les informations de ${editingEmployee.first_name} ${editingEmployee.last_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editFirstName">Prénom *</Label>
                <Input
                  id="editFirstName"
                  value={editEmployeeData.firstName}
                  onChange={(e) => setEditEmployeeData(prev => ({...prev, firstName: e.target.value}))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="editLastName">Nom *</Label>
                <Input
                  id="editLastName"
                  value={editEmployeeData.lastName}
                  onChange={(e) => setEditEmployeeData(prev => ({...prev, lastName: e.target.value}))}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="editEmail">Email *</Label>
              <Input
                id="editEmail"
                type="email"
                value={editEmployeeData.email}
                onChange={(e) => setEditEmployeeData(prev => ({...prev, email: e.target.value}))}
                required
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editPosition">Poste</Label>
                <Input
                  id="editPosition"
                  value={editEmployeeData.position}
                  onChange={(e) => setEditEmployeeData(prev => ({...prev, position: e.target.value}))}
                  placeholder="Développeur, Manager, etc."
                />
              </div>
              <div>
                <Label htmlFor="editDepartment">Département</Label>
                <Select 
                  value={editEmployeeData.department || "none"} 
                  onValueChange={(value) => setEditEmployeeData(prev => ({...prev, department: value === "none" ? "" : value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un département" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept.value} value={dept.value}>
                        {dept.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="editResidenceCountry">Pays de résidence</Label>
              <Input
                id="editResidenceCountry"
                value={editEmployeeData.residenceCountry}
                onChange={(e) => setEditEmployeeData(prev => ({...prev, residenceCountry: e.target.value}))}
                placeholder="France, Espagne, etc."
              />
            </div>
            <div>
              <Label htmlFor="editManager">Manager</Label>
              <Select 
                value={editEmployeeData.managerId || "none"} 
                onValueChange={(value) => setEditEmployeeData(prev => ({...prev, managerId: value === "none" ? "" : value}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un manager" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun manager</SelectItem>
                  {managerOptions
                    .filter(m => editingEmployee && m.id !== editingEmployee.id) // Can't be their own manager
                    .map(manager => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name} {manager.position ? `(${manager.position})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Le manager pourra valider les demandes de télétravail
              </p>
            </div>
            {seniorityLevels.length > 0 && (
              <div>
                <Label htmlFor="editSeniorityLevel">Niveau de séniorité</Label>
                <Select 
                  value={editEmployeeData.seniorityLevelId || "none"} 
                  onValueChange={(value) => setEditEmployeeData(prev => ({...prev, seniorityLevelId: value === "none" ? "" : value}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun niveau</SelectItem>
                    {seniorityLevels.map(level => (
                      <SelectItem key={level.id} value={level.id}>
                        {level.level_name} ({level.monthly_points_credit} pts/mois)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Définit les Nest Points mensuels automatiques selon la politique
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateEmployee}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeesTab;
